/**
 * WEB-3447 — ЛЁГКАЯ часть вычисляемых значений (решение владельца №5, повтор
 * Slimkit), которая остаётся в первом экране покупателя:
 *  - имена вычисляемых переменных — по ним страница решает, нужен ли воронке
 *    ленивый кусок `computedValues.ts` (`computedValuesDoor.ts`), а подстановка
 *    переменных — звать ли его;
 *  - сбор источников роста и веса из содержимого воронки — контекст
 *    переменных (`measureSources`) собирается одинаково, приехал кусок или нет,
 *    и шкала ИМТ «из вычисления» (кусок «reference») читает тот же контекст.
 *
 * Тяжёлая часть (счёт, пределы, слова уровней, даты, подсказки ИМТ) — в
 * `computedValues.ts`; он реэкспортирует отсюда всё, что раньше объявлял сам,
 * поэтому зовущие из кабинета и тесты пути импорта не меняют.
 */
import { resolvePickerUnitDefault } from '@/components/quiz-runtime/answerCollection';
import type { EditorBlock } from '@/store/editorSlice';

/**
 * Имена вычисляемых переменных — то, что автор пишет в {{…}}. Авторская
 * переменная с тем же именем (`variableName` блока) важнее: разбор имён
 * автора идёт раньше (`resolveVariable`, шаг 3).
 */
export const COMPUTED_VARIABLE_NAMES = [
  'bmi',
  'bmiLevel',
  'bmiCategory',
  'normalWeightMin',
  'normalWeightMax',
  'weightUnit',
  'currentWeight',
  'goalWeight',
  'weightLossPercent',
  'goalDate',
  'goalMonthYear',
] as const;
export type ComputedVariableName = (typeof COMPUTED_VARIABLE_NAMES)[number];

const COMPUTED_NAME_SET: ReadonlySet<string> = new Set(COMPUTED_VARIABLE_NAMES);

export function isComputedVariableName(
  name: string,
): name is ComputedVariableName {
  return COMPUTED_NAME_SET.has(name);
}

export interface MeasureSource {
  blockId: string;
  /** Единица блока по умолчанию — на случай, если посетитель не переключал. */
  unitDefault: string;
}

export interface MeasureSources {
  height?: MeasureSource;
  currentWeight?: MeasureSource;
  goalWeight?: MeasureSource;
}

/**
 * Источники роста и веса из содержимого воронки (все экраны по порядку).
 * Чистая, без исключений: мусор на входе → пустой объект.
 */
export function collectMeasureSources(content: unknown[]): MeasureSources {
  const out: MeasureSources = {};
  if (!Array.isArray(content)) return out;
  for (const screen of content) {
    if (typeof screen !== 'object' || screen === null) continue;
    const blocks = (screen as { blocks?: unknown }).blocks;
    if (!Array.isArray(blocks)) continue;
    for (const raw of blocks) {
      if (typeof raw !== 'object' || raw === null) continue;
      const block = raw as EditorBlock;
      if (typeof block.id !== 'string' || block.id === '') continue;
      if (block.type === 'height_picker') {
        if (!out.height)
          out.height = {
            blockId: block.id,
            unitDefault: resolvePickerUnitDefault(block),
          };
      } else if (block.type === 'weight_picker') {
        const source = {
          blockId: block.id,
          unitDefault: resolvePickerUnitDefault(block),
        };
        if (block.weightPickerRole === 'goal') {
          if (!out.goalWeight) out.goalWeight = source;
        } else if (!out.currentWeight) {
          out.currentWeight = source;
        }
      }
    }
  }
  return out;
}
